import { useState } from "react";

import { makePostApiCall } from "../../shared/makeApiCall";
import ModelErrorComponent from "../../components/modals/ModelErrorComponent";
import { BannerNotificationType } from "../../components/SimpleBanner";
import { subDays } from "date-fns";

export function useDashboardViewModel(logout) {
  const [emptyModal, setEmptyModelOpen] = useState({
    open: false,
    childComponent: null,
  });

  const [showSimpleNotification, setShowSimpleNotification] = useState({
    show: false,
    message: null,
    title: null,
    type: BannerNotificationType.Success,
  });

  // New: today's packaging & production aggregates
  const [loadingToday, setLoadingToday] = useState(false);
  const [packagingToday, setPackagingToday] = useState({
    totalUnits: 0,
    totalWeight: 0,
  });
  const [productionToday, setProductionToday] = useState({
    manufacturedWeight: 0,
    targetWeight: 0,
  });

  const [manufacturingAnalytics, setManufacturingAnalytics] = useState({
    range: null,
    stats: [],
    loading: true,
  });

  const [packagingAnalytics, setPackagingAnalytics] = useState({
    range: null,
    stats: [],
    loading: true,
  });

  const [errorToday, setErrorToday] = useState(null);

  const showErrorModal = (title, subtitle) => {
    setEmptyModelOpen({
      childComponent: (
        <ModelErrorComponent
          setOpen={setEmptyModelOpen}
          subtitle={subtitle}
          title={title}
        />
      ),
      open: true,
    });
  };

  const getPackagingAnalytics = async function (search, filterArg) {
    setPackagingAnalytics({ ...packagingAnalytics, loading: true });
    const payload = {
      filters: { ...filterArg, search: search },
    };
    let callResult = await makePostApiCall(`fetchPackagingAnalytics`, payload);
    setPackagingAnalytics({ ...packagingAnalytics, loading: false });
    if (callResult.success) {
      setPackagingAnalytics({ ...packagingAnalytics, ...callResult.data });
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const getManufacturingAnalytics = async function (search, filterArg) {
    setManufacturingAnalytics({ ...manufacturingAnalytics, loading: true });
    const payload = {
      filters: { ...filterArg, search: search },
    };
    let callResult = await makePostApiCall(
      `fetchManufacturingAnalytics`,
      payload,
    );
    setManufacturingAnalytics({ ...manufacturingAnalytics, loading: false });
    if (callResult.success) {
      setManufacturingAnalytics({
        ...manufacturingAnalytics,
        ...callResult.data,
      });
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal("Could not load analytics");
    }
  };

  // Dashboard-specific: fetch today's packaging and production aggregates
  const getTodayPackagingAndProduction = async function () {
    setLoadingToday(true);
    setErrorToday(null);

    try {
      // Try both calls in parallel
      // const productionFilter = {
      //   product: { value: -1 },
      //   category: { value: -1 },
      //   supervisor: { value: -1 },
      //   employee1: { value: -1 },
      //   employee2: { value: -1 },
      //   certification: { value: -1 },
      //   brand: { value: -1 },
      //   startDate: null,
      //   endDate: null,
      //   range: "today",
      // };
      const defaultFilters = {
        product: { value: -1, label: "All Products" },
        supervisor: { value: -1, label: "All Employees" },
        employee1: { value: -1, label: "All Employees" },
        employee2: { value: -1, label: "All Employees" },
        certification: { value: -1, label: "All Certifications" },
        recipe: { value: -1, label: "All Recipes" },
        grandTotal: null,
        startDate: subDays(new Date(), 7),
        endDate: new Date(),
        lot_number: null,
        search: null,
      };

      const packagingPayload = { filters: defaultFilters };
      const manufacturingPayload = { filters: defaultFilters };

      const [packagingCall, manufacturingCall] = await Promise.all([
        makePostApiCall("fetchPackagingAnalytics", packagingPayload),
        makePostApiCall("fetchManufacturingAnalytics", manufacturingPayload),
      ]);

      // Normalize packaging response
      if (packagingCall && packagingCall.success) {
        setPackagingToday({
          totalUnits: packagingCall.data.stats[0].stat,
          totalWeight: packagingCall.data.stats[1].stat,
        });
      } else {
        if (packagingCall && packagingCall.errorStatus === 401) logout();
      }

      // Normalize manufacturing response
      if (manufacturingCall && manufacturingCall.success) {
        setProductionToday({
          manufacturedWeight: manufacturingCall.data.stats[0].stat,
          targetWeight: manufacturingCall.data.stats[1].stat,
        });
      } else {
        if (manufacturingCall && manufacturingCall.errorStatus === 401)
          logout();
      }
    } catch (err) {
      setErrorToday(err?.message || "Could not load today analytics");
      showErrorModal("Could not load analytics", err?.message || null);
    } finally {
      setLoadingToday(false);
    }
  };

  return {
    loadingToday,
    packagingToday,
    productionToday,
    getTodayPackagingAndProduction,
    emptyModal,
    setEmptyModelOpen,
    showSimpleNotification,
    setShowSimpleNotification,
  };
}

export { useDashboardViewModel as DashboardViewModel };
